import React, { useEffect, useState, memo, useCallback, useRef } from "react";
import { className } from "utils";
import { ReactHlsPlayer } from "components/UI/HlsPlayer";
import { ReactComponent as PlayIcon } from "components/icons/play_icon.svg";
import { apiMain } from "services";

import "./VideoComponent.scss";

interface VideoComponentProps {
  video: string;
  sound: boolean;
  poster: string;
  canPlay: boolean;
  fullscreen: boolean;
  onPlay: () => void;
  onError?: (error: number, resetError: () => void) => void;
  isHls: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  playing: boolean;
  handleLoadedMetadata: () => void;
  handleTimeUpdate: () => void;
}

export const VideoComponent = (props: VideoComponentProps) => {
  const {
    video,
    sound,
    poster,
    canPlay,
    fullscreen,
    onPlay,
    onError,
    videoRef,
    playing,
    handleLoadedMetadata,
    handleTimeUpdate,
  } = props;

  const startLoadTimeRef = useRef(0);
  const canPlayTimeRef = useRef(0);

  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [isTriedToPlay, setIsTriedToPlay] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Stop playback on tab change.
  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        if (videoRef.current) {
          videoRef.current.pause();
          setPaused(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handler);

    return () => {
      document.removeEventListener("visibilitychange", handler);
    };
  }, [videoRef]);

  const play = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    // On some iOS devices, play() throws an exception:
    //
    // NotAllowedError: The request is not allowed by the user agent or the platform
    // in the current context, possibly because the user denied permission.
    //
    // See: https://trello.com/c/uBxK9w2r
    try {
      const playPromise = videoRef.current.play();

      if (playPromise !== undefined) {
        playPromise.then(() => {
          setPaused(false);
          onPlay();
        }).catch((e) => {
          console.warn("failed to play video", e);
          setPaused(true);
          setIsTriedToPlay(true);
        }).finally(() => {
          setIsTriedToPlay(true);
        });
      } else {
        setPaused(false);
        setIsTriedToPlay(true);
        onPlay();
      }
    } catch (e) {
      console.warn("failed to play video", e);
      setPaused(true);
      setIsTriedToPlay(true);
    }
  }, [onPlay, videoRef]);

  const pause = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.pause();
    setPaused(true);
  }, [videoRef]);

  useEffect(() => {
    if (!videoRef.current || error) {
      return;
    }

    if (canPlay && videoRef.current.paused && playing) {
      play();
      return;
    }

    if (!playing || !videoRef.current.paused) {
      pause();
      return;
    }

    if (!canPlay && !videoRef.current.paused) {
      pause();
    }
  }, [canPlay, play, pause, error, video, videoRef, playing]);

  const handleClick = useCallback(() => {
    if (!videoRef.current || error) {
      return;
    }

    videoRef.current.paused ? play() : pause();
  }, [error, play, pause, videoRef]);

  const handleError = useCallback(() => {
    const v = videoRef.current;

    if (v) {
      setError(v.networkState === v.NETWORK_NO_SOURCE ? "Error downloading the video file." : "Unknown error.");
      if (onError) {
        onError(v.networkState, () => {
          setError(null);
        });
      }
    } else {
      console.error("Video playback failed, but the ref is not yet ready.");
    }
  }, [videoRef, onError]);

  const handleLoadedData = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleLoadStart = useCallback(() => {
    startLoadTimeRef.current = performance.now();
  }, []);

  const handleCanPlay = useCallback(() => {
    if (canPlayTimeRef.current === 0) {
      canPlayTimeRef.current = performance.now();
      const duration = Math.ceil(canPlayTimeRef.current - startLoadTimeRef.current);

      console.debug(
        "loaded time(ms):",
        duration,
        new URL(props.video).pathname,
        "isHls:",
        props.isHls
      );

      apiMain.sendVideoLoaded({
        duration,
        isHLS: props.isHls
      });
    }
  }, [props.video, props.isHls]);

  const preload = fullscreen ? "none" : "metadata";

  return (
    <div className="videoWrapper">
      {
        !isLoaded && (
          <img src={poster} alt=""/>
        )
      }

      <ReactHlsPlayer
        className={className({
          error: !!error,
          isLoaded: isLoaded,
        })}
        isHls={props.isHls}
        controls={false}
        autoPlay={false}
        loop
        playsInline
        preload={preload}
        onCanPlay={handleCanPlay}
        onClick={handleClick}
        onError={handleError}
        onLoadStart={handleLoadStart}
        onLoadedData={handleLoadedData}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        muted={!sound}
        playerRef={videoRef}
        disablePictureInPicture={true}
        src={video}
      />

      {paused && isTriedToPlay && !error && (
        <PlayIcon
          className="playButton"
          onClick={handleClick}
        />
      )}

      {error && (
        <p className="error">{error}</p>
      )}
    </div>
  );
};

export const VideoComponentMemo = memo(VideoComponent);
